import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TemplateSummary } from '@/components/dashboard/TemplateSummary';
import { UserManagementPanel } from '@/components/dashboard/UserManagementPanel';
import { WeaponManagementPanel } from '@/components/dashboard/WeaponManagementPanel';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { DataStatePanel } from '@/components/shared/DataStatePanel';
import { EventStatusBadge } from '@/components/shared/EventStatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { Panel } from '@/components/shared/Panel';
import { StatCard } from '@/components/shared/StatCard';
import { eventRoutes } from '@/data/routes';
import { getEventRosterMetrics } from '@/features/events/selectors';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { sendDiscordTestDm } from '@/lib/repositories/adminRepository';
import { formatDateTime, getLocalTimeZoneLabel } from '@/utils/date';
import { formatAppRoleLabel, rolePriority } from '@/utils/roles';

export function DashboardPage(): JSX.Element {
  const {
    dashboardSnapshot,
    state,
    isLoading,
    error,
    refreshData,
    saveProfileAccess,
    setGuildMembershipActive,
    saveWeapon,
    deleteWeapon,
    syncWeaponCatalog,
    bundledWeaponCatalogCount,
    archiveEvent,
    deleteEventPermanently,
  } = useGuildData();
  const { profile, hasAnyRole } = useAuth();
  const canManageUsers = hasAnyRole(['admin', 'officer']);
  const isAdmin = hasAnyRole(['admin']);
  const formattedRoles =
    profile?.roles
      .slice()
      .sort((left, right) => rolePriority.indexOf(left) - rolePriority.indexOf(right))
      .map((role) => formatAppRoleLabel(role))
      .join(', ') ?? 'Member';
  const timeZoneLabel = getLocalTimeZoneLabel();
  const [massToDelete, setMassToDelete] = useState<(typeof state.events)[number] | null>(null);
  const [massDeleteConfirmation, setMassDeleteConfirmation] = useState('');
  const [isDeletingMass, setIsDeletingMass] = useState(false);
  const eventRows = useMemo(
    () =>
      [...state.events]
        .map((event) => ({
          ...event,
          ...getEventRosterMetrics(state, event.id),
        }))
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()),
    [state],
  );

  return (
    <AppShell>
      <PageHeader
        title="Staff Dashboard"
        description={`Mass ops, members, templates, and weapons. ${timeZoneLabel}.`}
        actions={
          <Link to={eventRoutes.create} className="button button--primary">
            Create Mass
          </Link>
        }
      />

      {isLoading ? <DataStatePanel title="Loading dashboard" description="Fetching live user, template, weapon, and audit data." /> : null}
      {!isLoading && error ? (
        <DataStatePanel title="Unable to load dashboard" description={error} actionLabel="Retry" onAction={() => void refreshData()} />
      ) : null}

      {!isLoading && !error ? (
        <>
          <div className="stats-grid">
            <StatCard label="Active Masses" value={dashboardSnapshot.stats.activeMasses} hint="Open or upcoming events" />
            <StatCard label="Pending Users" value={dashboardSnapshot.stats.pendingApprovals} hint="Approval queue to review" />
            <StatCard label="Active Weapons" value={dashboardSnapshot.stats.activeWeapons} hint="Catalog items enabled for templates" />
            <StatCard label="Archived Masses" value={dashboardSnapshot.stats.archivedMasses} hint="Historical records retained" />
          </div>

          <Panel
            title="Operational Scope"
            description="Live staff control for guild operations."
          >
            <p className="copy-block">
              Signed in as <strong>{profile?.displayName}</strong>. Current roles: {formattedRoles}. Admin handles member access and guild-level moderation, while
              officers and granted mass managers run daily mass operations.
            </p>
          </Panel>

          <Panel
            title="Mass Operations"
            description="Newest created masses appear first."
          >
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Mass</th>
                    <th>Local Start</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Required</th>
                    <th>Waitlist</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {eventRows.map((event) => (
                    <tr key={event.id}>
                      <td>
                        <strong>{event.title}</strong>
                      </td>
                      <td>{formatDateTime(event.startsAt)}</td>
                      <td>
                        <EventStatusBadge status={event.status} />
                      </td>
                      <td>
                        {event.totalJoined}
                      </td>
                      <td>{event.totalRequired}</td>
                      <td>{event.totalWaitlist}</td>
                      <td>
                        <div className="table-actions">
                          <Link to={eventRoutes.detail(event.id)} className="button button--ghost">
                            Open
                          </Link>
                          <Link to={eventRoutes.edit(event.id)} className="button button--secondary">
                            Edit Mass
                          </Link>
                          {event.status !== 'archived' ? (
                            <button
                              type="button"
                              className="button button--secondary"
                              onClick={() => {
                                if (window.confirm(`Archive "${event.title}" now? This keeps history but closes live management.`)) {
                                  void archiveEvent(event.id, true);
                                }
                              }}
                            >
                              Archive Mass
                            </button>
                          ) : null}
                          {isAdmin ? (
                            <button
                              type="button"
                              className="button button--danger"
                              onClick={() => {
                                setMassToDelete(event);
                                setMassDeleteConfirmation('');
                              }}
                            >
                              Delete Mass
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {canManageUsers ? (
            <UserManagementPanel
              users={state.profiles}
              currentProfileId={profile?.id}
              isAdmin={isAdmin}
              onSave={saveProfileAccess}
              onSetGuildMembershipActive={setGuildMembershipActive}
              onSendTestDm={sendDiscordTestDm}
            />
          ) : null}

          <TemplateSummary
            templates={dashboardSnapshot.templates}
            templateDrafts={state.templateDrafts}
            templateSlots={dashboardSnapshot.templateSlots}
            canManageTemplates={hasAnyRole(['admin', 'officer'])}
          />

          {canManageUsers ? (
            <WeaponManagementPanel
              weapons={dashboardSnapshot.weapons}
              onSave={saveWeapon}
              onDelete={deleteWeapon}
              onSyncBundledCatalog={syncWeaponCatalog}
              bundledCatalogCount={bundledWeaponCatalogCount}
            />
          ) : null}
        </>
      ) : null}

      <ConfirmationDialog
        open={Boolean(massToDelete)}
        title="Delete Mass"
        description="Are you sure you want to permanently delete this mass? This removes the mass, its parties, slots, signups, and related live references. This cannot be undone."
        confirmLabel="Delete Mass"
        confirmVariant="danger"
        confirmationLabel="Type the mass title to confirm"
        requiredText={massToDelete?.title ?? ''}
        confirmationValue={massDeleteConfirmation}
        onConfirmationValueChange={setMassDeleteConfirmation}
        isProcessing={isDeletingMass}
        onClose={() => {
          if (isDeletingMass) {
            return;
          }

          setMassToDelete(null);
          setMassDeleteConfirmation('');
        }}
        onConfirm={() => {
          if (!massToDelete) {
            return;
          }

          setIsDeletingMass(true);

          void deleteEventPermanently(massToDelete.id, massDeleteConfirmation)
            .then((wasDeleted) => {
              if (!wasDeleted) {
                return;
              }

              setMassToDelete(null);
              setMassDeleteConfirmation('');
            })
            .finally(() => {
              setIsDeletingMass(false);
            });
        }}
      />
    </AppShell>
  );
}
